// src/services/apiService.js
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '';
import { useAuthStore } from '@/stores/userStore';

// 添加 fetchWithTimeout 函数
const fetchWithTimeout = async (url, method, data, timeout, userToken) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
      },
      body: method !== 'get' ? JSON.stringify(data) : undefined,
      signal: controller.signal
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
};

const apiRequest = async (url, method, data, setLoading, timeout = 5000, userToken) => {
  try {
    if (setLoading) setLoading(true);
    const response = await fetchWithTimeout(url, method, data, timeout, userToken);

    if (!response.ok) {
      let errorMsg = response.statusText;
      let errorBody;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorBody = await response.clone().json();
          if (errorBody && (errorBody.ErrorMsg || errorBody.errorMsg)) {
            errorMsg = errorBody.ErrorMsg || errorBody.errorMsg;
          }
        }
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
      }
      const error = new Error(`${response.status} - ${errorMsg}`);
      error.status = response.status;
      error.statusText = errorMsg;
      error.response = { status: response.status }; // 添加response属性以兼容现有代码
      throw error;
    }

    // 检查响应内容类型
    const contentType = response.headers.get('content-type');
    let result;
    if (contentType && contentType.includes('application/json')) {
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error('Invalid JSON response from server');
      }
    } else {
      // 如果不是 JSON，返回文本内容
      result = { content: await response.text() };
    }
    return result;
  } catch (error) {
    console.error(`[apiRequest] 失败: ${url} ${method}`, error);
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const tts = (tts, setLoading, userToken) => {
  return apiRequest('/MSTTS/tts', 'post', tts, setLoading, 120000, userToken); // 增加TTS的超时时间到120秒
};

export const playArticle = (article, setLoading, userToken) => {
  return apiRequest(`/rss/playArticle`, 'post', article, setLoading, 120000, userToken); // 增加播放文章的超时时间到120秒
};

export const AdminNodes = (setLoading, userToken) => {
  console.log('AdminNodes called with token:', userToken); // 添加日志
  return apiRequest('/rss/AdminNodes', 'get', null, setLoading, 5000, userToken)
    .then(response => {
      console.log('AdminNodes response:', response); // 添加日志
      if (!Array.isArray(response)) {
        console.error('AdminNodes response is not an array:', response);
        return [];
      }
      return response;
    })
    .catch(error => {
      console.error('AdminNodes error:', error);
      return [];
    });
};

export const GetSeletedArticle = (setLoading, userToken, pageIndex = 1, pageSize = 10) => {
  return apiRequest(
    `/rss/GetSeletedArticle?pageIndex=${pageIndex}&pageSize=${pageSize}`,
    'get',
    null,
    setLoading,
    5000,
    userToken
  );
};

export const addOrUpdateNode = (node, setLoading, userToken) => {
  return apiRequest('/rss/AddOrUpdateNode', 'post', node, setLoading, 50000, userToken);
};

export const DelNode = (nodeId, setLoading, userToken) => {
  return apiRequest('/rss/DelNode', 'delete', { nodeId: nodeId }, setLoading, 5000, userToken);
};

export const LConn = (userToken, setLoading) => {
  return apiRequest('/rss/LConn', 'post', null, setLoading, 5000, userToken);
};

export const login = (userName, password, setLoading) => {
  return apiRequest(
    '/rss/lsetUp',
    'post',
    {
      UserName: userName,
      Password: password,
      Email: "maifeipin-qq.com"  // 保持默认邮箱
    },
    setLoading,
    5000
  );
};

export const fetchFeedGroups = (setLoading, userToken) => {
  return apiRequest('/rss/groups', 'get', {}, setLoading, 5000, userToken);
};

export const fetchRssNodes = (setLoading, userToken) => {
  return apiRequest('/rss/nodes', 'get', {}, setLoading, 5000, userToken)
    .then(response => {
      if (!Array.isArray(response)) {
        console.error('fetchRssNodes response is not an array:', response);
        return [];
      }
      return response;
    })
    .catch(error => {
      console.error('fetchRssNodes error:', error);
      return [];
    });
};

export const articleOpen = (idString, rssNodeId, setLoading) => {
  return apiRequest(`/rss/articleOpen`, 'post', { idString, rssNodeId }, setLoading);
};

export const fetchFeedsBySite = (siteId, pageIndex, pageSize, setLoading) => {
  return apiRequest(
    `/rss/GetFeedsBySite?siteId=${siteId}&pageIndex=${pageIndex}&pageSize=${pageSize}`,
    'get',
    {},
    setLoading,
    60000 // 增加获取文章列表的超时时间到60秒
  );
};

export const markAsRead = async (articleId, setLoading) => {
  try {
    const authStore = useAuthStore();
    if (!authStore.isAuthenticated) {
      throw new Error("No authentication token found");
    }

    return await apiRequest(
      `/rss/markAsRead`,
      'post',
      { articleId },
      setLoading,
      5000,
      authStore.token
    );
  } catch (error) {
    console.error('Error marking article as read:', error);
    throw error;
  }
};

export const toggleBookmark = async (articleId, setLoading) => {
  try {
    const authStore = useAuthStore();
    if (!authStore.isAuthenticated) {
      throw new Error("No authentication token found");
    }

    return await apiRequest(
      `/rss/toggleBookmark`,
      'post',
      { articleId },
      setLoading,
      5000,
      authStore.token
    );
  } catch (error) {
    console.error('Error toggling bookmark:', error);
    throw error;
  }
};

export const exportArticles = (params, setLoading) => {
  // 确保参数是对象
  if (typeof params !== 'object' || Array.isArray(params)) {
    params = {};
  }

  // 构建查询字符串
  const queryString = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  const url = `${API_BASE_URL}/RssAnalysis/export${queryString ? `?${queryString}` : ''}`;
  console.log('Export URL:', url); // 添加日志

  setLoading(true);
  return fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'text/csv'
    }
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .finally(() => {
      setLoading(false);
    });
};

export const getArticlesForExport = (params, setLoading) => {
  // 确保参数是对象
  if (typeof params !== 'object' || Array.isArray(params)) {
    params = {};
  }

  // 构建查询字符串
  const queryString = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  const url = `${API_BASE_URL}/RssAnalysis/getArticles${queryString ? `?${queryString}` : ''}`;
  console.log('Get Articles URL:', url); // 添加日志

  setLoading(true);
  return fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json'
    }
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .finally(() => {
      setLoading(false);
    });
};

export const getAIReview = (idString, rssNodeId, userToken) => {
  return apiRequest(
    '/RssAnalysis/AIReview',
    'post',
    { idString, rssNodeId },
    () => { }, // 不使用全局 loading
    60000, // 60 秒超时
    userToken
  );
};

export const deleteAIReview = (params, userToken) => {
  return apiRequest(
    '/RssAnalysis/AIReview/delete',
    'post',
    params,
    () => { }, // 不使用全局 loading
    30000, // 增加超时时间到 30 秒
    userToken
  );
};

export const getLogList = (setLoading, userToken, type = 'general') => {
  return apiRequest(`/rss/ListLogs?type=${type}`, 'get', null, setLoading, 30000, userToken)
    .then(response => {
      if (!Array.isArray(response)) {
        console.error('getLogList response is not an array:', response);
        return [];
      }
      return response;
    })
    .catch(error => {
      console.error('getLogList error:', error);
      if (error.status === 401) {
        // 使用 authStore 清除 token
        const authStore = useAuthStore();
        authStore.setlogout();
      }
      return [];
    });
};

export const getLogContent = (fileName, lines, setLoading, userToken, type = 'general') => {
  return apiRequest('/rss/GetLogs', 'post', { fileName, lines, type }, setLoading, 30000, userToken)
    .then(response => {
      if (!response || !response.lines) {
        console.error('getLogContent invalid response:', response);
        throw new Error('Invalid response format');
      }
      return response;
    })
    .catch(error => {
      console.error('getLogContent error:', error);
      if (error.status === 401) {
        // 只清除 token，不进行重定向
        localStorage.removeItem('userToken');
      }
      throw error;
    });
};

export const register = (userName, password, email, setLoading) => {
  return apiRequest(
    '/rss/register',
    'post',
    {
      UserName: userName,
      Password: password,
      Email: email
    },
    setLoading,
    5000
  );
};

export const getUsers = (token) => {
  return apiRequest('/admin/users', 'get', null, null, 5000, token);
};

export const updateUser = (user, token) => {
  return apiRequest('/admin/users/' + user.id, 'put', user, null, 5000, token);
};

export const deleteUser = (id, token) => {
  return apiRequest('/admin/users/' + id, 'delete', null, null, 5000, token);
};

export const resetUserPassword = (id, newPassword, token, userName) => {
  return apiRequest(`/admin/users/${id}/reset-password`, 'put', { newPassword, userName }, null, 5000, token);
};

export const getUserNodes = (userId, token) => {
  return apiRequest(`/admin/users/${userId}/nodes`, 'get', null, null, 5000, token);
};

export const getAllNodes = (token) => {
  return apiRequest('/admin/users/all-nodes', 'get', null, null, 5000, token);
};

export const setUserNodes = async (userId, nodeIds, token) => {
  if (!Array.isArray(nodeIds) || nodeIds.length === 0) {
    throw new Error('nodeIds 不能为空');
  }
  // 转成字符串数组
  const nodeIdsStr = nodeIds.map(id => String(id));
  // 直接传数组
  const res = await apiRequest(`/admin/users/${userId}/nodes`, 'post', nodeIdsStr, null, 5000, token);
  console.log('setUserNodes response:', res);
  return res;
};

export const apiService = {
  login,
  register,
  tts,
  fetchFeedGroups,
  fetchRssNodes,
  fetchFeedsBySite,
  articleOpen,
  LConn,
  DelNode,
  GetSeletedArticle,
  addOrUpdateNode,
  AdminNodes,
  playArticle,
  markAsRead,
  toggleBookmark,
  exportArticles,
  getArticlesForExport,
  getAIReview,
  deleteAIReview,
  getLogList,
  getLogContent,
  searchFeeds,
  getUsers,
  updateUser,
  deleteUser,
  resetUserPassword,
  getUserNodes,
  getAllNodes,
  setUserNodes
};

async function searchFeeds(params, setLoading) {
  try {
    if (setLoading) setLoading(true);

    // 构建查询字符串，处理数组参数
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // 对于数组类型的参数，每个值单独添加
          value.forEach(item => {
            queryParams.append(key, item);
          });
        } else {
          queryParams.append(key, value);
        }
      }
    });

    const url = `${API_BASE_URL}/RssAnalysis/search?${queryParams.toString()}`;
    console.log('Search URL:', url); // 添加日志

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('userToken')}`
      }
    });

    console.log('Response status:', response.status); // 添加日志

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText); // 添加日志
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Response data:', data); // 添加日志
    return data;
  } catch (error) {
    console.error('Error searching feeds:', error);
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
}
